import { useEffect, useRef } from "react";

type ObserverCallbackProps = {
  mutations: MutationRecord[];
  mutation: MutationRecord;
};
type OnChildRemovedParams = ObserverCallbackProps & {
  removedNodes: NodeList;
};
type OnChildAddedParams = ObserverCallbackProps & {
  addedNodes: NodeList;
};

const noop = (_props: any) => {};

/**
 * Wrapper around `MutationObserver`. `element` must be a reactive `state`, and not `ref.current` in order to keep observer up to date.
 * Wrap all callbacks in `useCallback`
 **/
export const useMutationObserverEffect = (
  element,
  {
    onChildAdded = noop,
    onChildRemoved = noop,
    onObserve = noop,
    onUnobserve = noop,
  }: {
    onChildAdded?: (_props: OnChildAddedParams) => void;
    onChildRemoved?: (_props: OnChildRemovedParams) => void;
    onObserve?: (_props: { element: Node }) => void;
    onUnobserve?: (_props: { element: Node }) => void;
  } = {},
  mutationObserverOptions: MutationObserverInit = {
    childList: true,
    subtree: true,
  },
): React.MutableRefObject<MutationObserver> => {
  const mutationObserver = useRef<MutationObserver>(null);

  useEffect(() => {
    if (!element) return () => {};
    mutationObserver.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // If mutation.type == 'childList' then a child was added or removed https://developer.mozilla.org/en-US/docs/Web/API/MutationRecord/type
        if (mutation.type === "childList") {
          if (mutation.removedNodes.length > 0) {
            onChildRemoved({
              removedNodes: mutation.removedNodes,
              mutations,
              mutation,
            });
          }
          if (mutation.addedNodes.length > 0) {
            onChildAdded({
              addedNodes: mutation.addedNodes,
              mutations,
              mutation,
            });
          }
        }
      });
    });

    mutationObserver.current.observe(element, mutationObserverOptions);

    onObserve({ element });

    return () => {
      mutationObserver.current.disconnect();
      onUnobserve({
        element,
      });
    };
  }, [element, onChildAdded, onChildRemoved, onObserve, onUnobserve]);

  return mutationObserver;
};
